import {useCallback, useEffect, useMemo, useState} from "react";
import {
	App as AntdApp,
	Button,
	ColorPicker,
	Drawer,
	Form,
	Input,
	Modal,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Typography
} from "antd";
import {PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, HistoryOutlined} from "@ant-design/icons";

import {
	createFieldRegistry,
	deleteFieldRegistry,
	listFieldRegistry,
	updateFieldRegistry
} from "@modules/fields/api/fieldRegistryApi.js";
import {normalizeFieldKey} from "@modules/fields/utils/fieldRegistry.js";
import {toHexColor} from "@modules/fields/utils/fieldColors.js";
import PromptHistoryTimeline from "@modules/prompt-history/components/PromptHistoryTimeline.jsx";

const {Text} = Typography;

const TYPE_OPTIONS = [
	{label: "Testo", value: "TEXT"},
	{label: "Testo lungo", value: "TEXTAREA"},
	{label: "Data", value: "DATE"},
	{label: "Selezione", value: "SELECT", disabled: true},
	{label: "Selezione multipla", value: "MULTISELECT", disabled: true}
];

// Il tipo di un campo tassonomia non è modificabile: i suoi valori sono riferimenti a
// un'anagrafica e il percorso corrispondente in FlyerCard.ai è dichiarato ObjectId, quindi
// portarlo a testo non converte i dati — li rende insalvabili. Il menu resta visibile (mostra
// com'è configurato il campo) ma in sola lettura: prima l'unico segnale era il 409 del backend
// DOPO il salvataggio, e la voce del menu che lo provocava era la più a portata di mano.
const TAXONOMY_TYPES = new Set(["SELECT", "MULTISELECT"]);

function isTypeLocked(field)
{
	if (!field)
		return false;

	return !!field.locked
		|| !!field.system
		|| TAXONOMY_TYPES.has(String(field.type || "").toUpperCase());
}

const DATE_MODE_OPTIONS = [
	{label: "Data singola", value: "single"},
	{label: "Intervallo", value: "range"}
];

const XML_EXPORT_TYPE_OPTIONS = [
	{label: "Tag annidato", value: "element"},
	{label: "Attributo di gabbia", value: "attribute"}
];

export default function AiDescriptionsTab()
{
	const {message} = AntdApp.useApp();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [fields, setFields] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingField, setEditingField] = useState(null);
	const [form] = Form.useForm();
	const [draftDescriptions, setDraftDescriptions] = useState({});
	const [searchTerm, setSearchTerm] = useState("");
	const [historyDrawerField, setHistoryDrawerField] = useState(null);
	const watchedName = Form.useWatch("name", form);

	const loadFields = useCallback(async () =>
	{
		setLoading(true);
		try
		{
			const result = await listFieldRegistry();
			if (result.ok !== false)
				setFields(Array.isArray(result.fields) ? result.fields.filter((f) => !f?.bannerOnly) : []);
			else
				message.error(result.error || "Impossibile caricare il catalogo campi");
		}
		catch (error)
		{
			message.error("Errore durante il caricamento dei campi");
		}
		finally
		{
			setLoading(false);
		}
	}, [message]);

	useEffect(() =>
	{
		loadFields();
	}, [loadFields]);

	const openCreateModal = useCallback(() =>
	{
		setEditingField(null);
		setModalOpen(true);
	}, []);

	const openEditModal = useCallback((field) =>
	{
		setEditingField(field);
		setModalOpen(true);
	}, []);

	const closeModal = useCallback(() =>
	{
		setModalOpen(false);
		setEditingField(null);
	}, []);

	useEffect(() =>
	{
		if (!modalOpen)
			return;

		if (editingField)
		{
			const draft = draftDescriptions[editingField.key];
			form.setFieldsValue({
				name: editingField?.name || "",
				type: editingField?.type || "TEXT",
				dateMode: editingField?.dateMode || "single",
				aiEnabled: !!editingField?.aiEnabled,
				aiDescription: draft !== undefined ? draft : (editingField?.aiDescription || ""),
				xmlTag: editingField?.xmlTag || "",
				xmlExportType: editingField?.xmlExportType || "element"
			});
		}
		else
		{
			form.resetFields();
		}
	}, [modalOpen, editingField, form, draftDescriptions]);

	const handleSaveField = useCallback(async () =>
	{
		const values = await form.validateFields();
		setSaving(true);
		try
		{
			const payload = {
				name: values.name,
				type: values.type,
				dateMode: values.type === "DATE" ? values.dateMode : undefined,
				aiEnabled: values.aiEnabled,
				aiDescription: values.aiEnabled ? values.aiDescription : "",
				xmlTag: values.xmlTag,
				xmlExportType: values.xmlExportType
			};

			const result = editingField
				? await updateFieldRegistry(editingField._id, payload)
				: await createFieldRegistry(payload);

			if (result.ok !== false)
			{
				message.success(editingField ? "Campo aggiornato" : "Campo creato");
				if (editingField)
				{
					setDraftDescriptions(prev =>
					{
						const next = {...prev};
						delete next[editingField.key];
						return next;
					});
				}
				closeModal();
				loadFields();
			}
			else
			{
				message.error(result.error || "Salvataggio non riuscito");
			}
		}
		catch (error)
		{
			message.error("Errore durante il salvataggio");
		}
		finally
		{
			setSaving(false);
		}
	}, [editingField, form, message, closeModal, loadFields]);

	const handleToggleAi = useCallback(async (field, enabled) =>
	{
		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {aiEnabled: enabled});
			if (result.ok !== false)
			{
				message.success(enabled ? "AI abilitata" : "AI disabilitata");
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [message, loadFields]);

	const handleChangeXmlExportType = useCallback(async (field, value) =>
	{
		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {xmlExportType: value});
			if (result.ok !== false)
			{
				message.success(value === "attribute" ? "Esportato come attributo" : "Esportato come tag annidato");
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [message, loadFields]);

	const handleChangeColor = useCallback(async (field, color) =>
	{
		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {color});
			if (result.ok !== false)
				loadFields();
			else
				message.error(result.error || "Aggiornamento colore non riuscito");
		}
		finally
		{
			setSaving(false);
		}
	}, [message, loadFields]);

	const handleDeleteField = useCallback(async (field) =>
	{
		setSaving(true);
		try
		{
			const result = await deleteFieldRegistry(field._id);
			if (result.ok !== false)
			{
				message.success("Campo eliminato");
				loadFields();
			}
			else
			{
				message.error(result.error || "Eliminazione non riuscita");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [message, loadFields]);

	const handleDescriptionChange = useCallback((fieldKey, value) =>
	{
		setDraftDescriptions(prev => ({
			...prev,
			[fieldKey]: value
		}));
	}, []);

	const handleSaveDescription = useCallback(async (field) =>
	{
		const draft = draftDescriptions[field.key];
		setSaving(true);
		try
		{
			const result = await updateFieldRegistry(field._id, {aiDescription: draft});
			if (result.ok !== false)
			{
				message.success("Descrizione aggiornata");
				setDraftDescriptions(prev =>
				{
					const next = {...prev};
					delete next[field.key];
					return next;
				});
				loadFields();
			}
			else
			{
				message.error(result.error || "Aggiornamento non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [draftDescriptions, message, loadFields]);

	const handleHistoryRestore = useCallback((content) =>
	{
		if (!historyDrawerField)
			return;

		handleDescriptionChange(historyDrawerField.key, content);
		setHistoryDrawerField(null);
		message.info("Versione ripristinata nell'editor");
	}, [historyDrawerField, handleDescriptionChange, message]);

	const filteredFields = useMemo(() =>
	{
		const query = searchTerm.trim().toLowerCase();
		if (!query)
			return fields;

		return fields.filter((field) =>
		{
			const name = String(field?.name || "").toLowerCase();
			const key = String(field?.key || "").toLowerCase();
			const xmlTag = String(field?.xmlTag || "").toLowerCase();
			const effectiveXmlTag = String(field?.xmlTag || field?.key || "").toLowerCase();
			return name.includes(query) || key.includes(query) || xmlTag.includes(query) || effectiveXmlTag.includes(query);
		});
	}, [fields, searchTerm]);

	const xmlTagPlaceholder = useMemo(() =>
	{
		if (editingField?.key)
			return editingField.key;

		const normalized = normalizeFieldKey(watchedName);
		return normalized || "es. nome_prodotto";
	}, [editingField?.key, watchedName]);

	const columns = useMemo(() => ([
		{
			title: "Campo",
			dataIndex: "name",
			key: "name",
			render: (_, field) => (
				<Space direction="vertical" size={2}>
					<Text strong>{field.name}</Text>
					<Text type="secondary" style={{fontSize: 12}}>{field.key}</Text>
				</Space>
			)
		},
		{
			title: "Tipo",
			dataIndex: "type",
			key: "type",
			render: (_, field) => (
				<Space>
					<Tag color="blue">{field.type}</Tag>
					{field.type === "DATE" ? <Tag>{field.dateMode === "range" ? "Range" : "Singola"}</Tag> : null}
				</Space>
			)
		},
		{
			title: "Colore",
			dataIndex: "color",
			key: "color",
			render: (_, field) => (
				<ColorPicker
					value={toHexColor(field.color)}
					disabledAlpha
					disabled={saving}
					onChangeComplete={(value) => handleChangeColor(field, value.toHexString())}
					showText={() => <Tag color={field.color || "blue"} style={{cursor: "pointer"}}>{field.name}</Tag>}
				/>
			)
		},
		{
			title: "Tag XML",
			dataIndex: "xmlTag",
			key: "xmlTag",
			render: (_, field) => (
				<Space direction="vertical" size={0}>
					<Text code>{field.xmlTag || field.key}</Text>
					{field.xmlTag ? <Text type="secondary" style={{fontSize: 12}}>Personalizzato</Text> : <Text type="secondary" style={{fontSize: 12}}>Default</Text>}
				</Space>
			)
		},
		{
			title: "Struttura XML",
			dataIndex: "xmlExportType",
			key: "xmlExportType",
			render: (_, field) => (
				<Select
					size="small"
					style={{minWidth: 170}}
					value={field.xmlExportType || "element"}
					options={XML_EXPORT_TYPE_OPTIONS}
					onChange={(value) => handleChangeXmlExportType(field, value)}
					disabled={saving}
				/>
			)
		},
		{
			title: "AI",
			dataIndex: "aiEnabled",
			key: "aiEnabled",
			render: (_, field) => (
				<Switch
					checked={field.aiEnabled}
					onChange={(checked) => handleToggleAi(field, checked)}
					disabled={saving}
				/>
			)
		},
		{
			title: "Azioni",
			key: "actions",
			render: (_, field) => (
				<Space>
					<Button
						icon={<EditOutlined/>}
						onClick={() => openEditModal(field)}
						disabled={field.locked || saving}
					/>
					<Popconfirm
						title="Eliminare il campo?"
						description="L'eliminazione rimuove il campo anche dai nodi organizzativi."
						onConfirm={() => handleDeleteField(field)}
						okText="Elimina"
						cancelText="Annulla"
						disabled={field.system || field.locked || saving}
					>
						<Button
							icon={<DeleteOutlined/>}
							danger
							disabled={field.system || field.locked || saving}
						/>
					</Popconfirm>
				</Space>
			)
		}
	]), [handleToggleAi, handleChangeXmlExportType, handleChangeColor, handleDeleteField, openEditModal, saving]);

	const drawerCurrentContent = historyDrawerField
		? (draftDescriptions[historyDrawerField.key] !== undefined
			? draftDescriptions[historyDrawerField.key]
			: (historyDrawerField.aiDescription || ""))
		: "";

	return (
		<Space direction="vertical" size={16} style={{width: "100%"}}>
			<Space align="center" style={{justifyContent: "space-between", width: "100%", marginTop: 10}} wrap>
				<Space align="center" size={12} wrap>
					<Text type="secondary">Gestisci i campi prodotto e le descrizioni AI associate.</Text>
					<Input.Search
						allowClear
						placeholder="Cerca campo..."
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
						style={{minWidth: 220}}
					/>
				</Space>
				<Button type="primary" icon={<PlusOutlined/>} onClick={openCreateModal}>
					Aggiungi campo
				</Button>
			</Space>

			<Table
				rowKey="key"
				loading={loading}
				columns={columns}
				dataSource={filteredFields}
				pagination={false}
				expandable={{
					expandedRowRender: (field) =>
					{
						if (!field.aiEnabled)
							return <Text type="secondary">AI disabilitata per questo campo.</Text>;

						const draftValue = draftDescriptions[field.key];
						const value = draftValue !== undefined ? draftValue : (field.aiDescription || "");

						return (
							<Space direction="vertical" size={8} style={{width: "100%"}}>
								<Input.TextArea
									rows={3}
									value={value}
									onChange={(e) => handleDescriptionChange(field.key, e.target.value)}
									placeholder="Inserisci la descrizione AI di default"
								/>
								<Space>
									<Button
										type="primary"
										icon={<SaveOutlined/>}
										onClick={() => handleSaveDescription(field)}
										disabled={saving}
									>
										Salva descrizione
									</Button>
									<Button
										icon={<HistoryOutlined/>}
										onClick={() => setHistoryDrawerField(field)}
									>
										Cronologia versioni
									</Button>
								</Space>
							</Space>
						);
					},
					rowExpandable: (field) => field.aiEnabled
				}}
			/>

			<Drawer
				title={`Cronologia versioni \u2014 ${historyDrawerField?.name || ""}`}
				width={480}
				open={!!historyDrawerField}
				onClose={() => setHistoryDrawerField(null)}
				destroyOnClose
			>
				{historyDrawerField && (
					<PromptHistoryTimeline
						entityType="FIELD_AI_DESCRIPTION"
						entityId={historyDrawerField._id}
						onRestore={handleHistoryRestore}
						currentContent={drawerCurrentContent}
					/>
				)}
			</Drawer>

			<Modal
				open={modalOpen}
				title={editingField ? "Modifica campo" : "Nuovo campo"}
				onCancel={closeModal}
				onOk={handleSaveField}
				okText={editingField ? "Salva" : "Crea"}
				confirmLoading={saving}
				destroyOnHidden
				forceRender
			>
				<Form form={form} layout="vertical" initialValues={{type: "TEXT", dateMode: "single", aiEnabled: false, xmlExportType: "element"}}>
					<Form.Item
						label="Nome campo"
						name="name"
						rules={[{required: true, message: "Inserisci un nome"}]}
					>
						<Input disabled={editingField?.locked}/>
					</Form.Item>
					<Form.Item
						label="Tipo"
						name="type"
						rules={[{required: true, message: "Seleziona un tipo"}]}
						extra={isTypeLocked(editingField)
							? "I campi a selezione attingono a un'anagrafica di valori: il tipo si cambia solo con una migrazione dei dati."
							: null}
					>
						<Select options={TYPE_OPTIONS} disabled={isTypeLocked(editingField)}/>
					</Form.Item>
					<Form.Item shouldUpdate={(prev, curr) => prev.type !== curr.type} noStyle>
						{({getFieldValue}) =>
							getFieldValue("type") === "DATE" ? (
								<Form.Item label="Modalit\u00e0 data" name="dateMode">
									<Select options={DATE_MODE_OPTIONS} disabled={editingField?.locked}/>
								</Form.Item>
							) : null
						}
					</Form.Item>
					<Form.Item label="AI">
						<Space size={8}>
							<Form.Item name="aiEnabled" valuePropName="checked" noStyle>
								<Switch/>
							</Form.Item>
							<Text>Abilita AI</Text>
						</Space>
					</Form.Item>
					<Form.Item shouldUpdate={(prev, curr) => prev.aiEnabled !== curr.aiEnabled} noStyle>
						{({getFieldValue}) =>
							getFieldValue("aiEnabled") ? (
								<Form.Item label="Descrizione AI" name="aiDescription">
									<Input.TextArea rows={3} placeholder="Descrizione AI di default"/>
								</Form.Item>
							) : null
						}
					</Form.Item>
					<Form.Item
						label="Tag XML"
						name="xmlTag"
						extra="Lascia vuoto per usare il nome campo normalizzato (default)."
					>
						<Input placeholder={xmlTagPlaceholder}/>
					</Form.Item>
					<Form.Item
						label="Struttura XML"
						name="xmlExportType"
						extra="Come esportare il campo nella gabbia: tag annidato oppure attributo del tag gabbia."
					>
						<Select options={XML_EXPORT_TYPE_OPTIONS}/>
					</Form.Item>
				</Form>
			</Modal>
		</Space>
	);
}
